import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { User as OidcUser } from 'oidc-client-ts';
import { getExam } from '@examflow/core';
import api, { type ActiveEntitlements, type User, setOidcAccessTokenGetter } from '../../lib/api';
import { chooseDefaultActiveExam } from '@examflow/learning-space';
import { getActiveExamType, setActiveExamType, setLanguage } from '../../lib/userPreferences';
import { getUserManager, getCurrentOidcUser, startWebOidcAuth } from '../../lib/oidc';

interface AuthContextType {
  user: User | null;
  entitlements: ActiveEntitlements | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (redirect?: string, mode?: 'signIn' | 'signUp') => void;
  signOut: () => void;
  logout: () => void;
  redeemCode: (code: string) => Promise<ActiveEntitlements | null>;
  refreshUser: () => Promise<ActiveEntitlements | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const POST_LOGIN_REDIRECT_KEY = 'examflow_post_login_redirect';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [oidcUser, setOidcUser] = useState<OidcUser | null>(null);
  const [oidcLoading, setOidcLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [entitlements, setEntitlements] = useState<ActiveEntitlements | null>(null);
  /** False until OIDC bootstrap + optional app-user bridge finished for this session tick. */
  const [sessionReady, setSessionReady] = useState(false);
  const entitlementsRef = useRef<ActiveEntitlements | null>(null);

  useEffect(() => {
    let mounted = true;
    void getCurrentOidcUser().then((u) => {
      if (mounted) {
        setOidcUser(u);
        setOidcLoading(false);
      }
    });
    const onLoaded = (u: OidcUser) => setOidcUser(u);
    const onUnloaded = () => setOidcUser(null);
    const userManager = getUserManager();
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    return () => {
      mounted = false;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
    };
  }, []);

  useEffect(() => {
    setOidcAccessTokenGetter(async () => {
      const u = await getCurrentOidcUser();
      return u?.access_token ?? null;
    });
  }, []);

  const applyActiveExamFromEntitlements = useCallback((data: ActiveEntitlements | null) => {
    const activeExam = chooseDefaultActiveExam(data, new Date(), getActiveExamType());
    if (!activeExam) return;
    const examInfo = getExam(activeExam);
    if (examInfo?.language.id) {
      setLanguage(examInfo.language.id);
    }
    setActiveExamType(activeExam);
  }, []);

  const refreshEntitlements = useCallback(async () => {
    const result = await api.licenses.me();
    const prev = entitlementsRef.current;
    const next = result.entitlements;
    if (JSON.stringify(prev) !== JSON.stringify(next)) {
      window.dispatchEvent(new CustomEvent('license:changed'));
    }
    entitlementsRef.current = next;
    setEntitlements(next);
    applyActiveExamFromEntitlements(next);
    return next;
  }, [applyActiveExamFromEntitlements]);

  const refreshUser = useCallback(async () => {
    try {
      const { user: userData } = await api.auth.me();
      setUser(userData);
      return await refreshEntitlements();
    } catch {
      setUser(null);
      setEntitlements(null);
      return null;
    }
  }, [refreshEntitlements]);

  const hasLiveOidcSession = Boolean(oidcUser && !oidcUser.expired);
  const hasExpiredOidcSession = Boolean(oidcUser?.expired);

  useEffect(() => {
    if (oidcLoading) return;

    let cancelled = false;

    const syncSession = async () => {
      setSessionReady(false);

      if (hasLiveOidcSession) {
        await refreshUser();
        if (!cancelled) setSessionReady(true);
        return;
      }

      // Expired access token: try silent renew before treating as logged out (avoids /login flash).
      if (hasExpiredOidcSession) {
        try {
          const renewed = await getUserManager().signinSilent();
          if (cancelled) return;
          if (renewed && !renewed.expired) {
            setOidcUser(renewed);
            // hasLiveOidcSession will flip and re-run this effect.
            return;
          }
        } catch {
          // Signed out below.
        }
        if (cancelled) return;
        setUser(null);
        setEntitlements(null);
        setSessionReady(true);
        return;
      }

      setUser(null);
      setEntitlements(null);
      setSessionReady(true);
    };

    void syncSession();
    return () => {
      cancelled = true;
    };
  }, [oidcLoading, hasLiveOidcSession, hasExpiredOidcSession, refreshUser]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setEntitlements(null);
      void getUserManager()
        .removeUser()
        .then(() => setOidcUser(null))
        .catch(() => setOidcUser(null));
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const signIn = useCallback((redirect?: string, mode: 'signIn' | 'signUp' = 'signIn') => {
    const finalRedirect = redirect || '/workspace';
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, finalRedirect);
    void startWebOidcAuth(mode);
  }, []);

  const signOut = useCallback(() => {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    setUser(null);
    setEntitlements(null);
    void getUserManager().signoutRedirect().catch(() => {
      void getUserManager()
        .removeUser()
        .then(() => setOidcUser(null))
        .catch(() => setOidcUser(null));
    });
  }, []);

  const redeemCode = async (code: string) => {
    const data = await api.licenses.redeem(code);
    if (data.user) {
      setUser(data.user);
    }
    // Prefer /licenses/me so prior Exam Spaces survive even if redeem payload is incomplete.
    let nextEntitlements = data.entitlements;
    try {
      const me = await api.licenses.me();
      nextEntitlements = me.entitlements;
    } catch {
      // Keep redeem payload when me() is unavailable.
    }
    setEntitlements(nextEntitlements);
    entitlementsRef.current = nextEntitlements;
    const redeemedExam = chooseDefaultActiveExam(nextEntitlements, new Date(), null);
    if (redeemedExam) {
      const examInfo = getExam(redeemedExam);
      if (examInfo?.language.id) {
        setLanguage(examInfo.language.id);
      }
      setActiveExamType(redeemedExam);
    }
    window.dispatchEvent(new CustomEvent('license:changed'));
    return nextEntitlements;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        entitlements,
        loading: oidcLoading || !sessionReady,
        isAuthenticated: !!user,
        signIn,
        signOut,
        logout: signOut,
        redeemCode,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
